/**
 * LOS CORREOS DEL SIMULADOR DE SEMANAS (08-10).
 *
 * Tres piezas que comparten «Simular un cliente» (AdminSimuladorPage) y «Simular todos»
 * (SimuladorPasoAPaso): el texto de un correo, la casilla para mandarlo de prueba y el
 * resultado del envío. El texto lo redacta el servidor; aquí solo se pinta.
 */
import React from 'react';
import { Mail, MailCheck, MailX } from 'lucide-react';
import { resumenDelEnvio } from '../lib/correosDelSimulador';
import AsuntoYCuerpo from './AsuntoYCuerpo';

/** El correo entero: asunto y texto. Si el servidor no pudo redactarlo, por qué. */
export const TextoDelCorreo = ({ correo, abierto = false, titulo }) => {
    if (!correo) return null;
    if (correo.sin_texto) {
        return (
            <p className="text-xs text-amber-300/80 mt-1.5 flex items-start gap-1.5" data-testid="sim-correo-sin-texto">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{titulo ? `${titulo}: ` : ''}correo sin texto. {correo.sin_texto}</span>
            </p>
        );
    }
    return (
        <details open={abierto} className="mt-1.5 rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] min-w-0"
            style={{ overflowWrap: 'anywhere' }} data-testid="sim-correo">
            <summary className="cursor-pointer select-none px-3 py-2 text-xs text-white/70 flex items-start gap-1.5">
                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FF671F]" />
                <span className="min-w-0 break-words">{titulo ? <span className="text-white/45">{titulo} · </span> : null}Correo: <span className="text-white">{correo.asunto}</span></span>
            </summary>
            <div className="px-3 pb-3 space-y-2">
                <AsuntoYCuerpo asunto={correo.asunto} cuerpo={correo.cuerpo} />
                {correo.nota && <p className="text-xs text-white/40">{correo.nota}</p>}
            </div>
        </details>
    );
};

/** La casilla. Solo sale si el servidor dice que aquí se puede mandar; si no, su motivo. */
export const CasillaDeCorreo = ({ donde, marcada, onCambio, desactivada }) => {
    if (!donde) return null;
    if (!donde.se_puede) {
        return <p className="text-xs text-white/35 w-full" data-testid="sim-correo-no-se-puede">{donde.motivo}</p>;
    }
    return (
        <label className="flex items-start gap-2 text-sm text-white/70 cursor-pointer select-none min-w-0">
            <input type="checkbox" checked={marcada} disabled={desactivada} data-testid="sim-mandar-correos"
                onChange={(e) => onCambio(e.target.checked)} className="w-4 h-4 mt-0.5 shrink-0 accent-[#FF671F]" />
            <span className="min-w-0">
                Mandar también el correo de prueba
                <span className="block text-xs text-white/40 break-words">
                    Sale de verdad, y solo a {donde.buzon}. Nunca al cliente. Como mucho {donde.tope} cada vez.
                    {' '}Se manda una vez: vuelve a marcarla para repetir.
                </span>
            </span>
        </label>
    );
};

/** Cómo fue el envío de prueba: el resumen y, por correo, a dónde salió o por qué no. */
export const EnvioDePrueba = ({ envio }) => {
    if (!envio) return null;
    const mandados = envio.mandados || [];
    const alguno = mandados.some((m) => m.salio);
    return (
        <div className={`rounded-xl border px-4 py-3 space-y-2 ${alguno ? 'border-sky-500/40 bg-sky-500/10' : 'border-amber-500/40 bg-amber-500/10'}`} data-testid="sim-envio">
            <p className={`text-sm font-medium flex items-start gap-2 ${alguno ? 'text-sky-200' : 'text-amber-200'}`}>
                {alguno ? <MailCheck className="w-4 h-4 mt-0.5 shrink-0" /> : <MailX className="w-4 h-4 mt-0.5 shrink-0" />}
                <span>{resumenDelEnvio(envio)}</span>
            </p>
            {mandados.length > 0 && (
                <ul className="space-y-1">
                    {mandados.map((m, i) => (
                        <li key={i} className="text-xs text-white/70 break-words">
                            <span className="text-white">{m.asunto}</span>{' · '}
                            {m.salio ? `salió a ${m.buzon}` : m.motivo}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
